import { createStore, combineReducers, applyMiddleware } from 'redux';
import { createBrowserHistory } from 'history';
import { createLogger } from 'redux-logger';
import createSagaMiddleware from 'redux-saga';
import trunk from 'redux-thunk';
import { connectRouter, routerMiddleware } from 'connected-react-router';
import { composeWithDevTools } from 'redux-devtools-extension';
import { reducer as notificationsReducer } from '@mrlannigan/reapop';
import { reducer as formReducer } from 'redux-form';
import formActionSaga from 'redux-form-saga';
import { loadingBarReducer } from 'react-redux-loading-bar';
import tracking from 'middleware/tracking';
import { get } from 'lodash';

// Helpers
import exposeGetState from 'middleware/exposeGetState';
import getAppRootUrlFromProcess from 'helpers/getAppRootUrlFromProcess';

// Modules
import auth from 'services/auth/duck';
import annotations from 'services/annotations/duck';
import analytics from 'services/analytics/duck';
import audits from 'services/audits/duck';
import campaign from 'services/campaign/duck';
import config from 'services/config/duck';
import dynamicProfiles from 'services/dynamicProfiles/duck';
import entities from 'services/entities/duck';
import embeddedLinks from 'services/embeddedLinks/duck';
import tactics from 'services/tactics/duck';
import channel from 'services/channel/duck';
import sem from 'services/sem/duck';
import social from 'services/social/duck';
import siteDirect from 'services/siteDirect/duck';
import theme from 'services/theme/duck';
import users from 'services/users/duck';
import feedback from 'services/feedback/duck';
import activities from 'services/activities/duck';
import layerTwo from 'services/layerTwo/duck';
import layout from 'services/layout/duck';
import routerReducer from 'services/router/duck';
import brands from 'services/brands/duck';
import ttd from 'services/ttd/duck';
import exports from 'services/exports/duck';
import offers from 'services/offers/duck';
import capabilityGroups from 'services/capabilityGroups/duck';
import capabilities from 'services/capabilities/duck';
import fullStory from 'services/fullstory/duck';
import youtube from 'services/youtube/duck';
import realValue from 'services/realValue/duck';
import hyperLocal from 'services/hyperlocal/duck';
import crossChannel from 'services/crossChannel/duck';
import smartguard from 'services/smartguard/duck';

// Sagas
import authSagas from 'services/auth/saga';
import annotationSagas from 'services/annotations/saga';
import analyticsSagas from 'services/analytics/saga';
import auditsSagas from 'services/audits/saga';
import configSagas from 'services/config/saga';
import campaignSagas from 'services/campaign/saga';
import dynamicProfilesSagas from 'services/dynamicProfiles/saga';
import entitiesSaga from 'services/entities/saga';
import embeddedLinksSaga from 'services/embeddedLinks/saga';
import tacticsSaga from 'services/tactics/saga';
import channelSaga from 'services/channel/saga';
import semSaga from 'services/sem/saga';
import socialSaga from 'services/social/saga';
import usersSaga from 'services/users/saga';
import feedbackSaga from 'services/feedback/saga';
import activitiesSaga from 'services/activities/saga';
import layerTwoSaga from 'services/layerTwo/saga';
import trackingSaga from 'services/tracking/saga';
import brandsSaga from 'services/brands/saga';
import ttdSaga from 'services/ttd/saga';
import exportsSaga from 'services/exports/saga';
import offersSaga from 'services/offers/saga';
import siteDirectSaga from 'services/siteDirect/saga';
import capabilityGroupsSaga from 'services/capabilityGroups/saga';
import capabilitiesSaga from 'services/capabilities/saga';
import youtubeSaga from 'services/youtube/saga';
import realValueSagas from 'services/realValue/saga';
import hyperLocalSagas from 'services/hyperlocal/saga';
import crossChannelSagas from 'services/crossChannel/saga';
import smartguardSagas from 'services/smartguard/saga';
import routerSaga from 'services/router/saga';

export const history = createBrowserHistory({ basename: getAppRootUrlFromProcess() });

export const storeFactory = (initialState, services = {}) => {
    const INIT_SAGAS = { type: '@@INIT-SAGAS' };
    const router = routerMiddleware(history);
    const saga = createSagaMiddleware();
    const logger = createLogger({
        predicate: (getState, action) => {
            if (get(action, 'type', []).includes('REGISTER_FIELD') && process.env.NODE_ENV === 'production') {
                return null;
            }
            return action.type;
        },
        collapsed: (getState, action, logEntry) => !logEntry.error
    });

    const rootReducer = combineReducers({
        auth,
        annotations,
        analytics,
        audits,
        campaign,
        config,
        dynamicProfiles,
        entities,
        embeddedLinks,
        form: formReducer,
        layout,
        loadingBar: loadingBarReducer,
        notifications: notificationsReducer(),
        router: routerReducer(connectRouter(history)),
        tactics,
        theme,
        users,
        feedback,
        activities,
        channel,
        sem,
        layerTwo,
        brands,
        ttd,
        exports,
        offers,
        siteDirect,
        social,
        capabilityGroups,
        capabilities,
        fullStory,
        youtube,
        realValue,
        hyperLocal,
        crossChannel,
        smartguard,
    });

    const store = createStore(
        rootReducer,
        initialState,
        composeWithDevTools(
            applyMiddleware(
                exposeGetState,
                router,
                saga,
                logger,
                trunk,
                ...tracking
            )
        ),
    );

    // Allows control of forms from sagas
    saga.run(authSagas, services);
    saga.run(annotationSagas, services);
    saga.run(analyticsSagas, services);
    saga.run(auditsSagas, services);
    saga.run(configSagas, services);
    saga.run(campaignSagas, services);
    saga.run(dynamicProfilesSagas, services);
    saga.run(entitiesSaga, services);
    saga.run(embeddedLinksSaga, services);
    saga.run(formActionSaga);
    saga.run(tacticsSaga, services);
    saga.run(usersSaga, services);
    saga.run(feedbackSaga, services);
    saga.run(activitiesSaga, services);
    saga.run(channelSaga, services);
    saga.run(semSaga, services);
    saga.run(siteDirectSaga, services);
    saga.run(layerTwoSaga, services);
    saga.run(trackingSaga, services);
    saga.run(brandsSaga, services);
    saga.run(ttdSaga, services);
    saga.run(exportsSaga, services);
    saga.run(offersSaga, services);
    saga.run(socialSaga, services);
    saga.run(capabilityGroupsSaga, services);
    saga.run(capabilitiesSaga, services);
    saga.run(youtubeSaga, services);
    saga.run(realValueSagas, services);
    saga.run(hyperLocalSagas, services);
    saga.run(crossChannelSagas, services);
    saga.run(smartguardSagas, services);
    saga.run(routerSaga, services);

    store.dispatch(INIT_SAGAS);
    return store;
};



// WEBPACK FOOTER //
// ./src/store.js